/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';

import { bindActionCreators } from 'redux';
import {
  saveLocalQuoteField,
  ORG_TYPE_STRING,
  setPaymentMethod,
} from 'admin/reducers/adminUpgradePage';
import assertions from 'lib/assertions';
import Columns2 from 'components/Columns2.jsx';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement.jsx';
import Heading from 'components/Heading.jsx';
import Input from 'components/Input.jsx';
import InputValueConstants from 'constants/InputValueConstants';
import PhoneNumberMaskedInput from 'components/PhoneNumberMaskedInput.jsx';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';
import Time from 'components/Time.jsx';
import AdminUpgradeQuoteTotalBox from 'admin/components/AdminUpgradeQuoteTotalBox.jsx';

import type { Fields } from 'admin/reducers/adminUpgradePage';

import './styles/AdminUpgradeRequestInvoiceForm.scss';

type Props = {
  quote: *,
  fields: Fields,
  saveLocalQuoteField: (name: string, value: *) => void,
  orgTypeString: string,
  setPaymentMethod: Function,
};

class AdminUpgradeRequestInvoiceForm extends React.Component<Props> {
  static trackingPrefix = 'admin_self_serve.request_invoice';

  componentDidMount() {
    this.track('view');
    this.props.setPaymentMethod('invoice');
  }

  componentWillUnmount() {
    // :sadface:
    //
    // the form pagination rely on the fact that we validate all
    // input like the backend. If an incorrect billing phone number
    // is saved in the store and the back button is pressed, the user
    // will not be able to to go the next page because the form will
    // error durring submission, and will not be able to edit the value
    // since the field isn't in the view!
    //
    // TODO validate the billing phone number area code like API does.
    this.props.saveLocalQuoteField('billingPhoneNumber', undefined);
  }

  track = (key: string) => {
    EventTracking.trackKey(`${AdminUpgradeRequestInvoiceForm.trackingPrefix}.${key}`);
  };

  render() {
    const { quote, orgTypeString } = this.props;

    return (
      <div className="AdminUpgradeRequestInvoiceForm">
        <Row>
          <div className="AdminUpgradeRequestInvoiceForm-reviewBox">
            <div className="AdminUpgradeRequestInvoiceForm-reviewBox-school">
              <div>
                <Text element="div" size="medium" weight="semibold">
                  Remind {orgTypeString} Plan for 1 year
                </Text>
                <Text element="div" color="thunder">
                  {quote.organization.name}
                </Text>
                <Text element="div" color="thunder">
                  <Time
                    date={new Date(quote.startDate)}
                    month="long"
                    day="numeric"
                    year="numeric"
                  />{' '}
                  to{' '}
                  <Time date={new Date(quote.endDate)} month="long" day="numeric" year="numeric" />
                </Text>
              </div>
            </div>
          </div>
          <AdminUpgradeQuoteTotalBox quote={quote} />
        </Row>
        <div>
          <FormElement
            label="Purchase order number"
            validate={e => {
              assertions.lengthLesserThanOrEqualTo(e.target.value, 80);
            }}
            onValidChange={e =>
              this.props.saveLocalQuoteField('purchaseOrderNumber', e.target.value)
            }
            onInvalidChange={() => this.props.saveLocalQuoteField('purchaseOrderNumber', undefined)}
            element={Input}
            elementProps={{
              placeholder: 'Optional',
              defaultValue: this.props.fields.get('purchaseOrderNumber'),
              onFocus: () => this.track('purchase_order.click'),
            }}
          />
        </div>
        <div>
          <Row size="small">
            <Heading level="2" levelStyle="6" divided>
              Billing contact details
            </Heading>
          </Row>
          <Columns2>
            <FormElement
              label="First name"
              validate={e => {
                assertions.lengthLesserThanOrEqualTo(
                  e.target.value,
                  InputValueConstants.FIRST_NAME_LENGTH
                );
                assertions.notEmpty(e.target.value);
              }}
              onValidChange={e =>
                this.props.saveLocalQuoteField('billingFirstName', e.target.value)
              }
              onInvalidChange={() => this.props.saveLocalQuoteField('billingFirstName', undefined)}
              element={Input}
              elementProps={{
                defaultValue: this.props.fields.get('billingFirstName'),
                onFocus: () => this.track('billing_first_name.click'),
              }}
            />
            <FormElement
              label="Last name"
              validate={e => {
                assertions.lengthLesserThanOrEqualTo(
                  e.target.value,
                  InputValueConstants.LAST_NAME_LENGTH
                );
                assertions.notEmpty(e.target.value);
              }}
              onValidChange={e => this.props.saveLocalQuoteField('billingLastName', e.target.value)}
              onInvalidChange={() => this.props.saveLocalQuoteField('billingLastName', undefined)}
              element={Input}
              elementProps={{
                defaultValue: this.props.fields.get('billingLastName'),
                onFocus: () => this.track('billing_last_name.click'),
              }}
            />
          </Columns2>
          <Columns2>
            <FormElement
              label="Email address"
              validate={e => {
                assertions.email(e.target.value);
                assertions.notEmpty(e.target.value);
              }}
              onValidChange={e => this.props.saveLocalQuoteField('billingEmail', e.target.value)}
              onInvalidChange={() => this.props.saveLocalQuoteField('billingEmail', undefined)}
              element={Input}
              elementProps={{
                defaultValue: this.props.fields.get('billingEmail'),
                onFocus: () => this.track('billing_email.click'),
              }}
            />
            <FormElement
              label="Phone number"
              validate={e => {
                assertions.number(e.raw, 'Invalid phone number.');
                assertions.completePhoneNumber(e.raw, 'Invalid phone number.');
              }}
              onValidChange={e => this.props.saveLocalQuoteField('billingPhoneNumber', e.raw)}
              onInvalidChange={() =>
                this.props.saveLocalQuoteField('billingPhoneNumber', undefined)
              }
              element={PhoneNumberMaskedInput}
              elementProps={{
                defaultValue: this.props.fields.get('billingPhoneNumber'),
                onFocus: () => this.track('billing_phone_number.click'),
              }}
            />
          </Columns2>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    fields: state.adminUpgradePage.fields,
    quote: state.adminUpgradePage.quote,
    orgTypeString: ORG_TYPE_STRING[state.adminUpgradePage.fields.get('orgType')],
  };
}

function mapDispatchToProps(dispatch: Function) {
  // $FlowIssue
  return bindActionCreators({ saveLocalQuoteField, setPaymentMethod }, dispatch);
}

export { AdminUpgradeRequestInvoiceForm };
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AdminUpgradeRequestInvoiceForm);
